import type { Animation } from '@ionic/core';
import type { EventEmitter } from '@stencil/core';
import { Component, Element, Event, h, Host, Method, Prop, State, Watch } from '@stencil/core';
import { Consumer } from '@sydle/ui-utils';
import { DISABLE_ANIMATION_VALUE } from '../../../utils/animations';
import {
  accordionBorderCloseAnimation,
  accordionIconCloseAnimation,
  accordionItemCloseAnimation,
} from './animations/close';
import {
  accordionBorderOpenAnimation,
  accordionIconOpenAnimation,
  accordionItemOpenAnimation,
} from './animations/open';

/**
 * @slot - Item's content.
 * @slot header - Content added in the item's header.
 */
@Component({
  tag: 'sy-accordion-item',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class AccordionItem {
  /**
   * Internal boolean to indicate when the property `expanded` was modified externally.
   */
  private internalExpanded: boolean;
  private contentEl: HTMLDivElement | undefined;
  private headerEl: HTMLDivElement | undefined;
  private iconEl: HTMLSyIconElement | undefined;
  private currentAnimation: Animation | undefined;
  private currentBorderAnimation: Animation | undefined;
  private currentIconAnimation: Animation | undefined;
  private borderValue: string;
  private dragElement: HTMLSyIconElement | undefined;
  private consumer: Consumer;

  @Element() el!: HTMLSyAccordionItemElement;

  /**
   * Item's label.
   */
  @Prop() label!: string;

  /**
   * Indicate if the item is expanded.
   */
  @Prop({ mutable: true, reflect: true }) expanded = false;

  /**
   * Callback function called before the accordion item is collapsed.
   * If returns 'false', the accordion item will not be collapsed.
   */
  @Prop() beforeCollapsed?: () => void | boolean | Promise<void | boolean>;

  /**
   * Callback function called before the accordion item is expanded.
   * If returns 'false', the accordion item will not be expanded.
   */
  @Prop() beforeExpanded?: () => void | boolean | Promise<void | boolean>;

  @Watch('expanded')
  expandedWatcher(): Promise<boolean> | undefined {
    if (this.expanded === this.internalExpanded) return;

    if (this.expanded) {
      return this.expand();
    }
    return this.collapse();
  }

  /**
   * Sets item's state to disabled.
   */
  @Prop({ mutable: true, reflect: true }) disabled = false;

  @Watch('disabled')
  disabledWatcher(): Promise<void> {
    if (this.disabled) {
      return this.disable();
    }
    return this.enable();
  }

  /**
   * Custom expanded icon. Used on the accordion header when it is expanded.
   * Not used when disabled-animations and disabled-animations are 'false' (default).
   */
  @Prop() expandedIcon = 'expand_more';

  /**
   * Custom collapsed icon. Used on the accordion header when it is collpased.
   * Used in both states when disabled-animations and disabled-animations are 'false' (default).
   */
  @Prop() collapsedIcon = 'chevron_right';

  /**
   * If true, actives drag function.
   */
  @Prop({ reflect: true }) enableDrag = false;

  @Watch('enableDrag')
  enableDragWatcher(): void {
    if (this.enableDrag) {
      this.createHandleComponent();
    } else {
      this.removeHandleComponent();
    }
  }

  /**
   * Optional custom animation to replace the default and be played when the component starts opening.
   * If 'none', the accordion-item won't have open animation.
   */
  @Prop() openAnimation?: ((accordionItem: HTMLElement) => Animation) | string;

  /**
   * Optional custom animation to replace the default and be played when the component starts closing.
   * If 'none', the accordion-item won't have close animation.
   */
  @Prop() closeAnimation?: ((accordionItem: HTMLElement) => Animation) | string;

  /**
   * Disables all animations
   */
  @Prop({ reflect: true }) disableAnimations = false;

  /**
   * Disable icon animation
   * True - toogle icons expandedIcon and collapsedIcon
   * False - rotate collapsedIcon 90deg (default)
   */
  @State() disableIconAnimation: boolean;

  /**
   * Emitted after an item is expanded or collapsed.
   */
  @Event() syAccordionItemDidToggle: EventEmitter<boolean>;

  /**
   * Emitted when an item starts to collapse.
   */
  @Event() syAccordionItemWillCollapse: EventEmitter<void>;

  /**
   * Emitted when an item starts to expand.
   */
  @Event() syAccordionItemWillExpand: EventEmitter<void>;

  /**
   * Toggle content between hidden or revealed.
   */
  @Method()
  async toggle(instant = false): Promise<boolean> {
    if (this.expanded) {
      return this.collapse(instant);
    }

    return this.expand(instant);
  }

  /**
   * Enable item.
   */
  @Method()
  async enable(): Promise<void> {
    this.disabled = false;
  }

  /**
   * Disable item: collapsed and can't be toggled.
   */
  @Method()
  async disable(instant = false): Promise<void> {
    this.disabled = true;
    await this.collapse(instant);
  }

  /**
   * Expand item's content.
   * If 'instant' is true, the accordion-item won't have open animation.
   */
  @Method()
  async expand(instant = false): Promise<boolean> {
    const isAlreadyExpanded = this.expanded && this.internalExpanded;
    if (isAlreadyExpanded) return false;

    const beforeExpandedResult = this.beforeExpanded && (await this.beforeExpanded());
    if (beforeExpandedResult === false) return false;

    this.syAccordionItemWillExpand.emit();

    if (!instant && !this.disableAnimations) {
      this.headerOpenAnimation();
    }

    this.internalExpanded = true;
    this.expanded = true;

    if (this.currentAnimation) {
      this.currentAnimation.stop();
    }

    if (!instant && !this.disableAnimations) {
      this.currentAnimation = this.getOpenAnimation();
      if (this.currentAnimation) {
        await this.currentAnimation.play();
      }
    }

    this.syAccordionItemDidToggle.emit(this.expanded);
    return true;
  }

  /**
   * Collapse item's content.
   * If 'instant' is true, the accordion-item won't have close animation.
   */
  @Method()
  async collapse(instant = false): Promise<boolean> {
    const isAlreadyCollapsed = !this.expanded && !this.internalExpanded;
    if (isAlreadyCollapsed) return false;

    const beforeCollapsedResult = this.beforeCollapsed && (await this.beforeCollapsed());
    if (beforeCollapsedResult === false) return false;

    this.internalExpanded = false;
    this.syAccordionItemWillCollapse.emit();

    if (this.currentAnimation) {
      this.currentAnimation.stop();
    }

    if (!instant && !this.disableAnimations) {
      this.headerCloseAnimation();
      this.currentAnimation = this.getCloseAnimation();
      if (this.currentAnimation) {
        await this.currentAnimation.play();
      }
    }

    this.expanded = false;
    this.syAccordionItemDidToggle.emit(this.expanded);
    return true;
  }

  /**
   * Get disableIconAnimation value
   */
  @Method()
  async getDisableIconAnimation(): Promise<boolean> {
    return this.disableIconAnimation;
  }

  private headerOpenAnimation() {
    if (this.currentBorderAnimation) {
      this.currentBorderAnimation.stop();
    }
    if (this.headerEl) {
      this.borderValue = getComputedStyle(this.headerEl).borderBottomLeftRadius;
      this.currentBorderAnimation = accordionBorderOpenAnimation(this.headerEl, this.borderValue);
      this.currentBorderAnimation.play();
    }

    if (!this.disableIconAnimation) {
      if (this.currentIconAnimation) {
        this.currentIconAnimation.stop();
      }
      if (this.iconEl) {
        this.currentIconAnimation = accordionIconOpenAnimation(this.iconEl);
        this.currentIconAnimation.play();
      }
    }
  }

  private headerCloseAnimation() {
    if (this.currentBorderAnimation) {
      this.currentBorderAnimation.stop();
    }
    if (this.headerEl) {
      this.currentBorderAnimation = accordionBorderCloseAnimation(this.headerEl, this.borderValue);
      this.currentBorderAnimation.play();
    }

    if (!this.disableIconAnimation) {
      if (this.currentIconAnimation) {
        this.currentIconAnimation.stop();
      }
      if (this.iconEl) {
        this.currentIconAnimation = accordionIconCloseAnimation(this.iconEl);
        this.currentIconAnimation.play();
      }
    }
  }

  private getOpenAnimation() {
    if (typeof this.openAnimation === 'string') {
      if (this.openAnimation === DISABLE_ANIMATION_VALUE) {
        return;
      }
      if (this.contentEl) {
        return accordionItemOpenAnimation(this.contentEl);
      }
      return;
    }
    const animationBuilder = this.openAnimation || accordionItemOpenAnimation;
    if (this.contentEl) {
      return animationBuilder(this.contentEl);
    }
  }

  private getCloseAnimation() {
    if (typeof this.closeAnimation === 'string') {
      if (this.closeAnimation === DISABLE_ANIMATION_VALUE) {
        return;
      }
      if (this.contentEl) {
        return accordionItemCloseAnimation(this.contentEl);
      }
      return;
    }
    const animationBuilder = this.closeAnimation || accordionItemCloseAnimation;
    if (this.contentEl) {
      return animationBuilder(this.contentEl);
    }
  }

  private handleHeaderClick(e) {
    const isInsideShadowDom = this.el.shadowRoot?.contains(e.target);
    const hasDataToggle = e.target.hasAttribute('data-sy-accordion-toggle');
    if (isInsideShadowDom || hasDataToggle) {
      this.toggle();
    }
  }

  private handleHeaderKeydown(event: KeyboardEvent) {
    if (this.disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  }

  private createHandleComponent() {
    if (this.enableDrag && !this.el.querySelector("[slot = 'sy-drag-icon]")) {
      this.dragElement = document.createElement('sy-icon');
      this.dragElement.slot = 'drag-icon';
      this.dragElement.name = 'drag_indicator';
      this.dragElement.classList.add('sy-accordion-drag-icon');
      this.el.append(this.dragElement);
    }
  }

  private removeHandleComponent() {
    if (this.dragElement) {
      this.el.removeChild(this.dragElement);
      this.dragElement = undefined;
    }
  }

  private setDisableIconAnimation(status: boolean): void {
    let newStatus = status;

    if (newStatus === undefined) {
      newStatus = this.expandedIcon !== 'expand_more' || this.collapsedIcon !== 'chevron_right';
    }

    this.disableIconAnimation = newStatus;
  }

  componentWillLoad() {
    this.consumer = new Consumer(this.el as Element);
    this.consumer.subscribe('disableIconAnimations', async (status: boolean) => this.setDisableIconAnimation(status), {
      suppress: true,
    });
  }

  componentDidLoad() {
    if (this.enableDrag) {
      this.createHandleComponent();
    }
  }

  render() {
    let arrowDirection: string;
    if (this.disableIconAnimation || this.disableAnimations) {
      arrowDirection = this.expanded ? this.expandedIcon : this.collapsedIcon;
    } else {
      arrowDirection = this.collapsedIcon;
    }

    return (
      <Host
        class={{
          'sy-drag-item': this.enableDrag,
          'icon-animation': !this.disableIconAnimation,
        }}
      >
        <div
          id="header-for-aria"
          class="sy-accordion-header"
          ref={(el) => (this.headerEl = el)}
          tabIndex={this.disabled ? -1 : 0}
          role="button"
          aria-label={this.label}
          aria-expanded={String(this.expanded)}
          aria-controls="content-inner-for-aria"
          onClick={(e) => {
            this.handleHeaderClick(e);
          }}
          onKeyDown={(e) => {
            this.handleHeaderKeydown(e);
          }}
          {...(this.disabled ? { disabled: true, 'aria-disabled': 'true' } : null)}
        >
          {this.enableDrag && (
            <div class="sy-drag-icon-area" aria-hidden="true">
              <slot name="drag-icon"></slot>
            </div>
          )}
          <sy-icon class="sy-accordion-icon" name={arrowDirection} ref={(el) => (this.iconEl = el)}></sy-icon>
          <div class="sy-accordion-title">{this.label ? this.label : ''}</div>
          <slot name="header"></slot>
        </div>
        <div class="sy-accordion-content" ref={(el) => (this.contentEl = el)}>
          <div
            id="content-inner-for-aria"
            class="sy-accordion-content-inner"
            role="region"
            aria-labelledby="header-for-aria"
          >
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
